const CART_ORDER_ITEM_LIMIT = 5;

const CartOrderLimit = {
  limit: CART_ORDER_ITEM_LIMIT,
  activeDrawerError: null,

  parseInputLimit(input, attr) {
    if (!input) return attr === 'max' ? null : 0;
    const value = attr === 'max' ? input.getAttribute('max') : input.getAttribute('min');
    if (value === null || value === '') return attr === 'max' ? null : 0;
    const parsed = parseInt(value, 10);
    if (Number.isNaN(parsed)) return attr === 'max' ? null : 0;
    return parsed;
  },

  hasInputMax(input) {
    return input?.hasAttribute('max');
  },

  getErrorMessage() {
    return (
      window.cartStrings?.orderLimitError ||
      'You can add up to 5 items to your cart per order.'
    );
  },

  getMaxQuantityMessage() {
    return (
      window.cartStrings?.orderLimitMaxQuantity ||
      'Maximum quantity added.'
    );
  },

  getAllMessages() {
    return [this.getErrorMessage(), this.getMaxQuantityMessage()];
  },

  syncQuantitySelectorError(errorMessage = false) {
    document.querySelectorAll('.product-form__error-message-wrapper--quantity').forEach((wrapper) => {
      const msgEl = wrapper.querySelector('.product-form__error-message');
      wrapper.toggleAttribute('hidden', !errorMessage);
      if (msgEl) msgEl.textContent = errorMessage || '';
    });

    if (errorMessage) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.scrollToPdpQuantityErrorIfNeeded());
      });
    }
  },

  isPdpMobileTabletViewport() {
    return window.matchMedia('(max-width: 1050px)').matches;
  },

  getPdpStickyBottomInset() {
    if (!this.isPdpMobileTabletViewport()) return 0;

    const stickyButtons = document.querySelector('.main-product-page .main-buy-btns');
    if (!stickyButtons) return 0;

    return stickyButtons.getBoundingClientRect().height + 16;
  },

  isPdpErrorInViewport(errorWrapper) {
    if (!errorWrapper || errorWrapper.hasAttribute('hidden')) return true;

    const rect = errorWrapper.getBoundingClientRect();
    if (rect.height === 0) return true;

    const bottomLimit = window.innerHeight - this.getPdpStickyBottomInset();
    return rect.top >= 0 && rect.bottom <= bottomLimit;
  },

  scrollToPdpQuantityErrorIfNeeded() {
    if (!this.isPdpMobileTabletViewport() || !document.querySelector('.main-product-page')) return;

    const errorWrapper = document.querySelector(
      '.main-product-page .product-form__error-message-wrapper--quantity:not([hidden])'
    );

    if (!errorWrapper || this.isPdpErrorInViewport(errorWrapper)) return;

    errorWrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
  },

  getQuantityErrorMessage(max, currentQty) {
    return (window.cartStrings?.quantityError || 'You can only add [quantity] of this item to your cart.').replace(
      '[quantity]',
      max ?? currentQty
    );
  },

  getDrawerLineItemCount() {
    return document.querySelectorAll('cart-drawer-items .cart-item, cart-items .cart-item').length;
  },

  isOrderLimitMessage(message) {
    return message?.trim() === this.getErrorMessage();
  },

  resolveDrawerErrorPlacement(line, message) {
    if (this.getDrawerLineItemCount() > 1 && this.isOrderLimitMessage(message)) {
      return 'footer';
    }
    return 'line';
  },

  getFirstCartLineIndex() {
    const input = document.querySelector(
      'cart-drawer-items .quantity__input[data-index], cart-items .quantity__input[data-index]'
    );
    return input?.dataset.index || '1';
  },

  getCartItemCount() {
    const cartRoot = document.querySelector('cart-drawer-items, cart-items');
    const count = parseInt(cartRoot?.dataset.itemCount, 10);
    return Number.isNaN(count) ? null : count;
  },

  syncDrawerErrorAtLimit(itemCount = this.getCartItemCount()) {
    if (itemCount === null || itemCount < CART_ORDER_ITEM_LIMIT) return;

    const line = this.getFirstCartLineIndex();
    this.showCartDrawerError(line, this.getErrorMessage());
  },

  setFooterErrorElement(cartErrors, message) {
    if (!cartErrors) return;

    const textEl = cartErrors.querySelector('.cart-item__error-text');
    if (textEl) {
      textEl.innerHTML = message || '';
    } else {
      cartErrors.textContent = message || '';
    }

    cartErrors.classList.toggle('cart-drawer__order-limit-error--visible', !!message);
  },

  clearDrawerErrorDom() {
    document.querySelectorAll('.cart-item__error-text').forEach((el) => {
      el.innerHTML = '';
      el.closest('.cart-item__error')?.classList.remove('cart-item__error--visible');
    });

    const cartErrors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
    this.setFooterErrorElement(cartErrors, '');
  },

  applyDrawerError() {
    if (!this.activeDrawerError) return;

    const { placement, line, message } = this.activeDrawerError;
    this.clearDrawerErrorDom();

    if (placement === 'footer') {
      const cartErrors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
      this.setFooterErrorElement(cartErrors, message);
      return;
    }

    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);

    if (lineItemError) {
      const textEl = lineItemError.querySelector('.cart-item__error-text');
      if (textEl) textEl.innerHTML = message;
      lineItemError.classList.add('cart-item__error--visible');
    }
  },

  showCartDrawerError(line, message) {
    if (!line || !message) return;

    const placement = this.resolveDrawerErrorPlacement(line, message);
    this.activeDrawerError = { placement, line, message };
    this.applyDrawerError();
  },

  showCartLineError(line, message) {
    this.showCartDrawerError(line, message);
  },

  clearCartLineError(line) {
    if (!this.activeDrawerError) return;
    if (this.activeDrawerError.placement === 'line' && this.activeDrawerError.line === String(line)) {
      this.activeDrawerError = null;
      this.clearDrawerErrorDom();
    }
  },

  showProductFormError(message) {
    document.querySelectorAll('product-form').forEach((form) => {
      if (typeof form.handleErrorMessage === 'function') {
        form.handleErrorMessage(message);
      }
    });
  },

  wouldExceedOnAdd(currentItemCount, quantityToAdd) {
    return currentItemCount + quantityToAdd > CART_ORDER_ITEM_LIMIT;
  },

  wouldExceedOnLineChange(currentItemCount, currentLineQty, newLineQty) {
    const newTotal = currentItemCount - currentLineQty + newLineQty;
    return newTotal > CART_ORDER_ITEM_LIMIT;
  },

  remainingCapacity(currentItemCount) {
    return Math.max(0, CART_ORDER_ITEM_LIMIT - currentItemCount);
  },

  clearErrors() {
    const messages = this.getAllMessages();
    this.activeDrawerError = null;

    document.querySelectorAll('product-form').forEach((form) => {
      const errorEl = form.querySelector('.product-form__error-message');
      if (errorEl && messages.includes(errorEl.textContent.trim()) && typeof form.handleErrorMessage === 'function') {
        form.handleErrorMessage();
      }
    });

    this.syncQuantitySelectorError(false);
    this.clearDrawerErrorDom();
  },

  syncCartItemCount(itemCount) {
    if (itemCount === undefined) return;
    document.querySelectorAll('cart-drawer-items, cart-items').forEach((el) => {
      el.dataset.itemCount = itemCount;
    });
  },

  init() {
    if (typeof subscribe !== 'function') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const itemCount = event.cartData?.item_count;
      if (itemCount !== undefined) {
        this.syncCartItemCount(itemCount);
      }
      if (itemCount !== undefined && itemCount < CART_ORDER_ITEM_LIMIT) {
        this.clearErrors();
      } else if (itemCount !== undefined && itemCount >= CART_ORDER_ITEM_LIMIT) {
        requestAnimationFrame(() => this.syncDrawerErrorAtLimit(itemCount));
      } else if (this.activeDrawerError) {
        requestAnimationFrame(() => this.applyDrawerError());
      }
    });
  },
};

window.CartOrderLimit = CartOrderLimit;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    CartOrderLimit.init();
    CartOrderLimit.syncDrawerErrorAtLimit();
  });
} else {
  CartOrderLimit.init();
  CartOrderLimit.syncDrawerErrorAtLimit();
}
